import { Box, Card, CardContent, Grid, Typography, MenuItem, Select, FormControl, InputLabel } from "@mui/material";
import { useEffect, useState } from "react";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import moment from "moment";
import CardComp from "shared/components/card";
import Chart from "shared/components/chart";
import { toastMessage } from "shared/components/toast";
import useBreadcrumbs from "shared/customHooks/useBreadCrumbs";
import { routeConstant } from "shared/routes/routesConstant";
import { getAnalyticsInstalls } from "shared/services/productManagerService";
import styles from "./style.module.scss";

function AppAnalyticsInstalls() {
  const [loading, setLoading] = useState<boolean>(false);
  const [platform, setPlatform] = useState<string>("all");
  const [dateRange, setDateRange] = useState<any>({
    startDate: moment().subtract(30, "days").toDate(),
    endDate: new Date(),
  });
  const [analyticsData, setAnalyticsData] = useState<any>(null);

  useBreadcrumbs([
    {
      label: routeConstant.dashboard.title,
      route: routeConstant.dashboard.path,
    },
    {
      label: routeConstant.appAnalytics.title,
      route: routeConstant.appAnalytics.path,
    },
    {
      label: routeConstant.appAnalyticsInstalls.title,
      route: routeConstant.appAnalyticsInstalls.path,
    },
  ]);

  const onChangeDate = (dates: any) => {
    const [start, end] = dates;
    setDateRange({
      startDate: start,
      endDate: end,
    });
  };

  const fetchInstallsData = () => {
    setLoading(true);
    const dateFrom = dateRange.startDate
      ? moment(dateRange.startDate).format("YYYY-MM-DD")
      : undefined;
    const dateTo = dateRange.endDate
      ? moment(dateRange.endDate).format("YYYY-MM-DD")
      : undefined;
    const platformFilter = platform !== "all" ? platform : undefined;

    getAnalyticsInstalls(dateFrom, dateTo, platformFilter)
      .then(({ data }) => {
        setAnalyticsData(data?.data);
        setLoading(false);
      })
      .catch((err) => {
        console.log("err", err?.response?.data?.message);
        toastMessage("error", err?.response?.data?.message);
        setLoading(false);
      });
  };

  useEffect(() => {
    if (dateRange.endDate) {
      fetchInstallsData();
    }
  }, [dateRange, platform]);

  const summaryCards = [
    {
      title: "Total Installs",
      value: analyticsData?.total || 0,
    },
    {
      title: "New Installs",
      value: analyticsData?.new_installs || 0,
    },
    {
      title: "Reinstalls",
      value: analyticsData?.reinstalls || 0,
    },
  ];

  const chartData = {
    labels: analyticsData?.by_date?.map((item: any) => item.date) || [],
    datasets: [
      {
        label: "Daily Installs",
        data: analyticsData?.by_date?.map((item: any) => item.count) || [],
        borderColor: "rgb(75, 192, 192)",
        backgroundColor: "rgba(75, 192, 192, 0.2)",
        tension: 0.1,
      },
    ],
  };

  return (
    <Box className={styles.container}>
      <Box className={styles.header}>
        <Typography variant="h4">App Installs Analytics</Typography>
        <Box className={styles.filters}>
          <FormControl className={styles.platformSelect}>
            <InputLabel>Platform</InputLabel>
            <Select
              value={platform}
              onChange={(e) => setPlatform(e.target.value)}
              label="Platform"
            >
              <MenuItem value="all">All Platforms</MenuItem>
              <MenuItem value="ios">iOS</MenuItem>
              <MenuItem value="android">Android</MenuItem>
            </Select>
          </FormControl>
          <DatePicker
            selected={dateRange.startDate}
            onChange={onChangeDate}
            startDate={dateRange.startDate}
            endDate={dateRange.endDate}
            selectsRange
            dateFormat="dd/MM/yyyy"
            placeholderText="Select date range"
            className={styles.dateInput}
          />
        </Box>
      </Box>

      <Grid container spacing={3} className={styles.statsContainer}>
        {summaryCards.map((card, index) => (
          <Grid item xs={12} sm={6} md={4} key={index}>
            <CardComp
              title={card.title}
              stats={card.value}
              isLoading={loading}
            />
          </Grid>
        ))}
      </Grid>

      <Grid container spacing={3} className={styles.chartContainer}>
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Installs by Platform
              </Typography>
              {analyticsData?.by_platform && (
                <Box className={styles.platformStats}>
                  {analyticsData.by_platform.map((item: any) => (
                    <Box key={item.platform} className={styles.platformItem}>
                      <Typography variant="body1">
                        {item.platform.toUpperCase()}
                      </Typography>
                      <Typography variant="body1" fontWeight="bold">
                        {item.count}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Install Trend
              </Typography>
              {analyticsData?.by_date && (
                <Chart
                  labels={chartData.labels}
                  sales={chartData.datasets[0].data}
                />
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
}

export default AppAnalyticsInstalls;
